import { inject, Injectable, InjectionToken } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

// ── Config token — provided in app.config.ts ──────────────────────────────────

export interface OrgContextConfig {
  apiUrl: string;
  baseDomain: string;
}

export const ORG_CONTEXT_CONFIG = new InjectionToken<OrgContextConfig>(
  'ORG_CONTEXT_CONFIG'
);

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Resolves the Auth0 organisation slug from the current hostname.
 *
 * Examples (baseDomain = 'ka-boom.app'):
 *   mcsgrp-dev.ka-boom.app  → 'mcsgrp-dev'
 *   ka-boom.app             → undefined  (root domain, no org context)
 *   localhost               → undefined  (local dev, no org context)
 */
export function getOrgFromHostname(baseDomain: string): string | undefined {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') return undefined;
  if (hostname === baseDomain) return undefined;

  if (hostname.endsWith(`.${baseDomain}`)) {
    const subdomain = hostname.slice(0, hostname.length - baseDomain.length - 1);
    if (subdomain && !subdomain.includes('.')) return subdomain;
  }

  return undefined;
}

// ── Service ───────────────────────────────────────────────────────────────────

export interface OrgContext {
  slug: string;
  orgId: string;
  displayName: string;
  logoUrl?: string;
}

@Injectable({ providedIn: 'root' })
export class OrgContextService {
  /**
   * Uses HttpBackend directly to bypass Auth0 interceptor — the tenant
   * resolve endpoint is anonymous and called before the user logs in.
   */
  private http = new HttpClient(inject(HttpBackend));
  private config = inject(ORG_CONTEXT_CONFIG);

  private orgContext$: Observable<OrgContext | null> | null = null;

  readonly slug = getOrgFromHostname(this.config.baseDomain);

  resolve(): Observable<OrgContext | null> {
    if (!this.slug) return of(null);

    if (!this.orgContext$) {
      this.orgContext$ = this.http
        .get<OrgContext>(
          `${this.config.apiUrl}/api/tenants/resolve?slug=${this.slug}`
        )
        .pipe(
          catchError((err) => {
            console.error(
              `[OrgContext] Failed to resolve org for slug "${this.slug}":`,
              err
            );
            return of(null);
          }),
          shareReplay(1)
        );
    }

    return this.orgContext$;
  }
}
